
import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { Patient, Prescriber, Project, AnalysisCatalogItem } from '../../types';
import { Loader2, Briefcase } from 'lucide-react';

interface CreateSampleModalProps {
  onClose: () => void;
  onSampleCreated: () => void;
  patients: Patient[];
  fetchPatients: () => void;
}

const CreateSampleModal: React.FC<CreateSampleModalProps> = ({ onClose, onSampleCreated, patients, fetchPatients }) => {
  // State
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
  const [isSubmittingPatient, setIsSubmittingPatient] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  // Prescriber State
  const [prescribers, setPrescribers] = useState<Prescriber[]>([]);
  const [showPrescriberDropdown, setShowPrescriberDropdown] = useState(false);
  const [isCreatingPrescriber, setIsCreatingPrescriber] = useState(false);
  const [newPrescriber, setNewPrescriber] = useState({ name: '', onm: '' });

  // Projects State
  const [projects, setProjects] = useState<Project[]>([]);
  const [catalogItems, setCatalogItems] = useState<AnalysisCatalogItem[]>([]);

  // Forms
  const [formData, setFormData] = useState({
    patientId: '',
    sampleType: 'Sang',
    collectionDate: new Date().toISOString().split('T')[0],
    prescriber: '',
    projectId: '' // Linked Project
  });

  const [newPatient, setNewPatient] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: 'M',
    uniqueId: ''
  });

  // Fetch Data
  useEffect(() => {
    const fetchDependencies = async () => {
        if (isSupabaseConfigured()) {
            const [prescRes, projRes, catRes] = await Promise.all([
                supabase.from('prescribers').select('*').order('name'),
                supabase.from('projects').select('*').order('name'),
                supabase.from('analysis_catalog').select('*')
            ]);
            
            if (prescRes.data) setPrescribers(prescRes.data as Prescriber[]);
            if (projRes.data) setProjects(projRes.data as Project[]);
            if (catRes.data) setCatalogItems(catRes.data as AnalysisCatalogItem[]);

        } else {
            // Mock
            setPrescribers([
                { id: '1', name: 'Dr. Rakoto (ONM 1234)', created_at: '' },
                { id: '2', name: 'Dr. Randrianasolo (ONM 5678)', created_at: '' }
            ]);
            setProjects([
                { id: '1', name: 'TB-Speed', code: 'TBS', default_analyses: ['1'], created_at: '', active: true }
            ]);
        }
    };
    fetchDependencies();
  }, []);

  // Derived state
  const filteredPatientsInModal = patients.filter(p => 
    (p.last_name?.toLowerCase() || '').includes(patientSearch.toLowerCase()) ||
    (p.first_name?.toLowerCase() || '').includes(patientSearch.toLowerCase()) ||
    (p.unique_id?.toLowerCase() || '').includes(patientSearch.toLowerCase())
  );

  const filteredPrescribers = prescribers.filter(p => 
    p.name.toLowerCase().includes(formData.prescriber.toLowerCase())
  );

  // Helpers
  const generatePatientId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const prefix = `P-${year}${month}-`;
    
    // Filtrer les patients qui ont un ID commençant par le préfixe du mois en cours
    const currentMonthIds = (patients || [])
      .map(p => p.unique_id)
      .filter(id => id && id.startsWith(prefix));

    let maxSequence = 0;
    currentMonthIds.forEach(id => {
      // On retire le préfixe pour ne garder que la séquence
      const suffix = id.replace(prefix, '');
      const seq = parseInt(suffix, 10);
      if (!isNaN(seq) && seq > maxSequence) maxSequence = seq;
    });

    const nextSequence = (maxSequence + 1).toString().padStart(4, '0');
    return `${prefix}${nextSequence}`;
  };

  const startPatientCreation = () => {
      setNewPatient({
        firstName: '',
        lastName: '',
        birthDate: '',
        gender: 'M',
        uniqueId: generatePatientId()
      });
      setIsCreatingPatient(true);
      setShowPatientDropdown(false);
  };

  const handleCreatePrescriber = async () => {
      if (!newPrescriber.name || !newPrescriber.onm) return alert("Veuillez remplir le nom et le numéro ONM");
      const fullName = `Dr. ${newPrescriber.name} (ONM ${newPrescriber.onm})`;
      
      if (isSupabaseConfigured()) {
          const { data, error } = await supabase.from('prescribers').insert([{ name: fullName }]).select();
          if (!error && data) {
              setPrescribers([...prescribers, data[0] as Prescriber]);
              setFormData({ ...formData, prescriber: fullName });
              setIsCreatingPrescriber(false);
              setNewPrescriber({ name: '', onm: '' });
          } else {
              alert("Erreur: " + error?.message);
          }
      } else {
          // Mock
          const mockP = { id: Math.random().toString(), name: fullName, created_at: '' };
          setPrescribers([...prescribers, mockP]);
          setFormData({ ...formData, prescriber: fullName });
          setIsCreatingPrescriber(false);
          setNewPrescriber({ name: '', onm: '' });
      }
  };

  // Actions
  const handleCreateSample = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.patientId) return alert("Veuillez sélectionner un patient");
    if (!formData.prescriber) return alert("Veuillez indiquer le prescripteur (Médecin/ONM)");

    // Determine initial analyses based on project
    let initialAnalyses: string[] = [];
    if (formData.projectId) {
        const project = projects.find(p => p.id === formData.projectId);
        if (project && project.default_analyses) {
            // Find names of analyses from IDs, making sure to expand Panels
            const expandedIds: string[] = [];
            
            project.default_analyses.forEach(id => {
                 const item = catalogItems.find(i => i.id === id);
                 if (item) {
                     if (item.sub_analyses && item.sub_analyses.length > 0) {
                         // It's a panel, add its children
                         expandedIds.push(...item.sub_analyses);
                     } else {
                         // Regular analysis
                         expandedIds.push(id);
                     }
                 }
            });

            // Convert IDs to Names and remove duplicates
            initialAnalyses = Array.from(new Set(
                catalogItems
                .filter(i => expandedIds.includes(i.id))
                .map(i => i.name)
            ));
        }
    }

    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('samples').insert([{
        patient_id: formData.patientId,
        sample_type: formData.sampleType,
        collection_date: formData.collectionDate,
        prescriber: formData.prescriber,
        project_id: formData.projectId || null,
        status: initialAnalyses.length > 0 ? 'processing' : 'received',
        analyses: initialAnalyses.length > 0 ? initialAnalyses : []
      }]);

      if (!error) {
        onSampleCreated();
        onClose();
      } else {
        alert("Erreur: " + error.message);
      }
    } else {
      onSampleCreated();
      onClose();
    }
  };

  const handleQuickCreatePatient = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingPatient(true);

    if (!isSupabaseConfigured()) {
      // Mock logic
      setIsCreatingPatient(false);
      setIsSubmittingPatient(false);
      return;
    }

    const { data, error } = await supabase.from('patients').insert([{
      first_name: newPatient.firstName,
      last_name: newPatient.lastName,
      birth_date: newPatient.birthDate,
      gender: newPatient.gender,
      unique_id: newPatient.uniqueId
    }]).select();

    if (!error && data) {
      const created = data[0] as Patient;
      fetchPatients(); // Refresh parent list
      selectPatient(created);
      setIsCreatingPatient(false);
    } else {
      alert("Erreur lors de la création du patient: " + (error?.message || "Erreur inconnue"));
    }
    setIsSubmittingPatient(false);
  };

  const selectPatient = (patient: Patient) => {
    setFormData({ ...formData, patientId: patient.id });
    setPatientSearch(`${patient.last_name} ${patient.first_name} (${patient.unique_id})`);
    setShowPatientDropdown(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
          <h3 className="font-bold text-lg text-slate-800">
            {isCreatingPatient ? "Nouveau Patient" : "Réception d'échantillon"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">&times;</button>
        </div>
        <div className="overflow-y-auto p-6">
          {isCreatingPatient ? (
            <form onSubmit={handleQuickCreatePatient} className="space-y-4">
               <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
                    <input required disabled={isSubmittingPatient} value={newPatient.firstName} onChange={e => setNewPatient({...newPatient, firstName: e.target.value})} className="w-full p-2 border border-slate-300 rounded disabled:bg-slate-100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                    <input required disabled={isSubmittingPatient} value={newPatient.lastName} onChange={e => setNewPatient({...newPatient, lastName: e.target.value})} className="w-full p-2 border border-slate-300 rounded disabled:bg-slate-100" />
                  </div>
               </div>

               <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Date Naissance</label>
                    <input type="date" required disabled={isSubmittingPatient} value={newPatient.birthDate} onChange={e => setNewPatient({...newPatient, birthDate: e.target.value})} className="w-full p-2 border border-slate-300 rounded disabled:bg-slate-100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Sexe</label>
                    <select disabled={isSubmittingPatient} value={newPatient.gender} onChange={e => setNewPatient({...newPatient, gender: e.target.value})} className="w-full p-2 border border-slate-300 rounded disabled:bg-slate-100">
                       <option value="M">Masculin</option>
                       <option value="F">Féminin</option>
                    </select>
                  </div>
               </div>

               <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ID Unique (Code-barres)</label>
                  <input required disabled={isSubmittingPatient} value={newPatient.uniqueId} onChange={e => setNewPatient({...newPatient, uniqueId: e.target.value})} className="w-full p-2 border border-slate-300 rounded disabled:bg-slate-100 font-mono" placeholder="Ex: P-202512-0001" />
                  <p className="text-xs text-slate-500 mt-1">Généré automatiquement</p>
               </div>

               <div className="flex justify-end gap-2 mt-4">
                   <button type="button" disabled={isSubmittingPatient} onClick={() => setIsCreatingPatient(false)} className="px-4 py-2 border rounded hover:bg-slate-50 disabled:opacity-50">Retour</button>
                   <button 
                     type="submit" 
                     disabled={isSubmittingPatient}
                     className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {isSubmittingPatient && <Loader2 size={16} className="animate-spin" />}
                        Créer
                    </button>
               </div>
            </form>
          ) : (
            <form onSubmit={handleCreateSample} className="space-y-4">
               {/* PATIENT SELECTION */}
               <div className="relative">
                 <label className="block text-sm font-medium mb-1">Patient</label>
                 <input 
                    className="w-full p-2 border border-slate-300 rounded" 
                    value={patientSearch}
                    onChange={e => {setPatientSearch(e.target.value); setShowPatientDropdown(true);}}
                    onFocus={() => setShowPatientDropdown(true)}
                    // Delay onBlur to allow clicking on dropdown items
                    onBlur={() => setTimeout(() => setShowPatientDropdown(false), 200)}
                    placeholder="Rechercher..."
                 />
                 {showPatientDropdown && (
                     <div className="absolute z-10 w-full bg-white border shadow-lg max-h-40 overflow-auto rounded-b">
                         {filteredPatientsInModal.length > 0 ? filteredPatientsInModal.map(p => (
                             <div key={p.id} onClick={() => selectPatient(p)} className="p-2 hover:bg-slate-50 cursor-pointer text-sm">
                                 <span className="font-bold">{p.last_name}</span> {p.first_name} <span className="text-slate-400 text-xs">({p.unique_id})</span>
                             </div>
                         )) : (
                             <div className="p-2 text-xs text-slate-400 italic">Aucun patient trouvé</div>
                         )}
                         <div onClick={startPatientCreation} className="p-2 text-indigo-600 cursor-pointer border-t hover:bg-indigo-50 font-medium text-sm sticky bottom-0 bg-white">
                            + Nouveau Patient
                         </div>
                     </div>
                 )}
                 {formData.patientId && <div className="text-xs text-green-600 mt-1">✓ Patient sélectionné</div>}
               </div>
               
               {/* PROJECT SELECTION */}
               <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                    <label className="block text-xs font-bold text-indigo-800 mb-1 flex items-center gap-1">
                        <Briefcase size={12} /> Projet / Étude (Optionnel)
                    </label>
                    <select 
                        className="w-full p-2 text-sm border border-indigo-200 rounded bg-white"
                        value={formData.projectId}
                        onChange={e => setFormData({...formData, projectId: e.target.value})}
                    >
                        <option value="">-- Aucun (Prélèvement Standard) --</option>
                        {projects.filter(p => p.active !== false).map(p => (
                            <option key={p.id} value={p.id}>{p.name} {p.code ? `(${p.code})` : ''}</option>
                        ))}
                    </select>
                    {formData.projectId && (
                        <p className="text-[10px] text-indigo-600 mt-1 italic">
                            * Les analyses par défaut du projet seront ajoutées automatiquement.
                        </p>
                    )}
               </div>

               {/* PRESCRIBER SELECTION */}
               <div>
                 <label className="block text-sm font-medium mb-1">Prescripteur (Médecin / N° ONM)</label>
                 {isCreatingPrescriber ? (
                     <div className="bg-slate-50 p-3 rounded border border-slate-200 animate-in fade-in zoom-in-95">
                         <p className="text-xs font-bold text-slate-700 mb-2 uppercase">Nouveau Prescripteur</p>
                         <div className="grid grid-cols-2 gap-2 mb-3">
                             <div>
                                 <label className="text-[10px] font-bold text-slate-500 block mb-1">Nom Médecin</label>
                                 <input 
                                    className="w-full p-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                                    value={newPrescriber.name}
                                    onChange={e => setNewPrescriber({...newPrescriber, name: e.target.value})}
                                    placeholder="Ex: Rakoto"
                                    autoFocus
                                 />
                             </div>
                             <div>
                                 <label className="text-[10px] font-bold text-slate-500 block mb-1">N° ONM</label>
                                 <input 
                                    className="w-full p-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                                    value={newPrescriber.onm}
                                    onChange={e => setNewPrescriber({...newPrescriber, onm: e.target.value})}
                                    placeholder="Ex: 1234"
                                 />
                             </div>
                         </div>
                         <div className="flex justify-end gap-2">
                             <button type="button" onClick={() => setIsCreatingPrescriber(false)} className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded">Annuler</button>
                             <button type="button" onClick={handleCreatePrescriber} className="px-2 py-1 text-xs bg-indigo-600 text-white hover:bg-indigo-700 rounded font-medium">Enregistrer</button>
                         </div>
                     </div>
                 ) : (
                     <div className="relative">
                        <input 
                            required
                            className="w-full p-2 border border-slate-300 rounded" 
                            value={formData.prescriber}
                            onChange={e => {
                                setFormData({...formData, prescriber: e.target.value});
                                setShowPrescriberDropdown(true);
                            }}
                            onFocus={() => setShowPrescriberDropdown(true)}
                            onBlur={() => setTimeout(() => setShowPrescriberDropdown(false), 200)}
                            placeholder="Rechercher..."
                        />
                        {showPrescriberDropdown && (
                             <div className="absolute z-10 w-full bg-white border shadow-lg max-h-40 overflow-auto rounded-b">
                                 {filteredPrescribers.length > 0 ? filteredPrescribers.map(p => (
                                     <div key={p.id} onClick={() => {
                                         setFormData({...formData, prescriber: p.name});
                                         setShowPrescriberDropdown(false);
                                     }} className="p-2 hover:bg-slate-50 cursor-pointer text-sm">
                                         {p.name}
                                     </div>
                                 )) : (
                                     <div className="p-2 text-xs text-slate-400 italic">Aucun résultat</div>
                                 )}
                                 <div 
                                    onClick={() => { setIsCreatingPrescriber(true); setShowPrescriberDropdown(false); }}
                                    className="p-2 text-indigo-600 cursor-pointer border-t hover:bg-indigo-50 font-medium text-sm sticky bottom-0 bg-white"
                                 >
                                    + Nouveau Prescripteur
                                 </div>
                             </div>
                        )}
                     </div>
                 )}
               </div>

               <div>
                 <label className="block text-sm font-medium mb-1">Type d'échantillon</label>
                 <select className="w-full p-2 border border-slate-300 rounded" value={formData.sampleType} onChange={e => setFormData({...formData, sampleType: e.target.value})}>
                    <option>Sang</option><option>Urine</option><option>Ecouvillon</option><option>Selles</option><option>Crachats</option><option>LCR</option>
                 </select>
               </div>
               <button type="submit" className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 transition-colors font-medium">Réceptionner</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateSampleModal;
